import React, { useState, useEffect, useCallback } from "react";
import {
    Box, Button, IconButton, Input, List, ListItem, ListItemIcon, ListItemText, Typography,
    Breadcrumbs, TextField, Divider, LinearProgress, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
    Select, MenuItem, FormControl, InputLabel
} from "@mui/material";
import FolderIcon from "@mui/icons-material/Folder";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import HomeIcon from "@mui/icons-material/Home";
import FolderSharedIcon from "@mui/icons-material/FolderShared";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import RestoreIcon from "@mui/icons-material/Restore";
import StorageIcon from "@mui/icons-material/Storage";
import ShareIcon from "@mui/icons-material/Share";
import TranslateIcon from "@mui/icons-material/Translate";
import { useNavigate, useLocation } from "react-router-dom";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import SearchIcon from "@mui/icons-material/Search";
import { FileIcon, defaultStyles } from "react-file-icon";
import CloudQueueIcon from "@mui/icons-material/CloudQueue";
import axios from "axios";
import { getStoredToken } from "../utils/authToken";

// Format file size helper
function formatBytes(bytes) {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    if (bytes < 1024 * 1024 * 1024) return (bytes / 1024 / 1024).toFixed(1) + " MB";
    return (bytes / 1024 / 1024 / 1024).toFixed(2) + " GB";
}

function formatFileSize(bytes) {
    if (bytes < 1024 * 1024) {
        return (bytes / 1024).toFixed(1) + " KB";
    }
    return (bytes / 1024 / 1024).toFixed(1) + " MB";
}

// Helper: plan label from quota
function getPlanFromQuota(quota) {
    if (!quota) return "Unknown";
    if (quota >= 100 * 1024 * 1024 * 1024) return "Scale (Unlimited)"; // Assume scale or high custom
    if (quota >= 50 * 1024 * 1024 * 1024) return "Growth (50 GB)";
    if (quota >= 5 * 1024 * 1024 * 1024) return "Essential (5 GB)";
    if (quota >= 500 * 1024 * 1024) return "Free (500 MB)";
    return "Custom";
}

// Breadcrumbs helper
async function buildBreadcrumbs(fileId, token) {
    let crumbs = [];
    let current = fileId;
    while (current) {
        const { data } = await axios.get(`/api/files/${current}`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        if (!data) break;
        crumbs.unshift(data);
        current = data.parent;
    }
    return crumbs;
}

// Extension helper
function getFileExtension(name = "") {
    const parts = name.split(".");
    if (parts.length < 2) return "";
    return parts.pop().toLowerCase();
}

function formatCreatedDate(value) {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric"
    });
}

function getFileSecondaryText(file) {
    const parts = [];
    if (file.type === "file" && typeof file.size === "number") {
        parts.push(`${(file.size / 1024).toFixed(1)} KB`);
    } else if (file.type === "folder") {
        parts.push("Folder");
    }
    const createdLabel = formatCreatedDate(file.createdAt);
    if (createdLabel) {
        parts.push(`Created ${createdLabel}`);
    }
    return parts.join(" • ");
}

const FileManager = () => {
    const [files, setFiles] = useState([]);
    const [parent, setParent] = useState(null);
    const [breadcrumbs, setBreadcrumbs] = useState([]);
    const [folderDialog, setFolderDialog] = useState(false);
    const [newFolderName, setNewFolderName] = useState("");
    const [uploading, setUploading] = useState(false);
    const [sidebar, setSidebar] = useState("home");
    const [usage, setUsage] = useState({ used: 0, quota: 5 * 1024 * 1024 * 1024 });
    const [uploadProgress, setUploadProgress] = useState(0);

    // Sharing dialog state
    const [shareDialogOpen, setShareDialogOpen] = useState(false);
    const [shareTargetFile, setShareTargetFile] = useState(null);
    const [teamMembers, setTeamMembers] = useState([]);
    const [selectedMember, setSelectedMember] = useState("");
    const [shareError, setShareError] = useState("");
    const [searchQuery, setSearchQuery] = useState("");

    // External sharing dialog state
    const [externalShareDialogOpen, setExternalShareDialogOpen] = useState(false);
    const [externalShareTargetFile, setExternalShareTargetFile] = useState(null);
    const [externalEmail, setExternalEmail] = useState("");
    const [externalShareError, setExternalShareError] = useState("");

    // Google Drive state
    const [googleFiles, setGoogleFiles] = useState([]);
    const [isGoogleConnected, setIsGoogleConnected] = useState(false);
    const [googleLoading, setGoogleLoading] = useState(false);
    const [googleParent, setGoogleParent] = useState(null); // null means root
    const [googleBreadcrumbs, setGoogleBreadcrumbs] = useState([]);

    // Dropbox state
    const [dropboxFiles, setDropboxFiles] = useState([]);
    const [isDropboxConnected, setIsDropboxConnected] = useState(false);
    const [dropboxLoading, setDropboxLoading] = useState(false);
    const [dropboxParent, setDropboxParent] = useState(""); // empty string means root
    const [dropboxBreadcrumbs, setDropboxBreadcrumbs] = useState([]);

    // OneDrive state
    const [oneDriveFiles, setOneDriveFiles] = useState([]);
    const [isOneDriveConnected, setIsOneDriveConnected] = useState(false);
    const [oneDriveLoading, setOneDriveLoading] = useState(false);
    const [oneDriveParent, setOneDriveParent] = useState(null); // null means root
    const [oneDriveBreadcrumbs, setOneDriveBreadcrumbs] = useState([]);

    const navigate = useNavigate();
    const location = useLocation();

    const token = getStoredToken();

    // Check query params for google connection
    useEffect(() => {
        const query = new URLSearchParams(location.search);
        if (query.get("google") === "connected") {
            setIsGoogleConnected(true);
            setSidebar("google");
            navigate("/dashboard/file-manager", { replace: true });
        }
        if (query.get("dropbox") === "connected") {
            setIsDropboxConnected(true);
            setSidebar("dropbox");
            navigate("/dashboard/file-manager", { replace: true });
        }
    }, [location.search, navigate]);

    // Check integration status on mount
    useEffect(() => {
        const checkStatus = async () => {
            try {
                const { data } = await axios.get('/api/integrations/status', {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (data.google) {
                    setIsGoogleConnected(true);
                }
                if (data.dropbox) {
                    setIsDropboxConnected(true);
                }
                if (data.onedrive) {
                    setIsOneDriveConnected(true);
                }
            } catch (err) {
                console.error("Failed to check integration status", err);
            }
        };
        checkStatus();
    }, [token]);


    // Fetch files for current parent folder or bin/shared
    const fetchFiles = useCallback(async () => {
        try {
            if (sidebar === "bin") {
                const { data } = await axios.get("/api/files/bin", {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setFiles(data);
            } else if (sidebar === "shared") {
                const { data } = await axios.get("/api/files/shared", {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setFiles(data);
            } else {
                const { data } = await axios.get("/api/files", {
                    params: { parent: parent || "" },
                    headers: { Authorization: `Bearer ${token}` },
                });
                setFiles(data);
            }
        } catch (err) {
            setFiles([]);
        }
    }, [parent, token, sidebar]);

    // Build breadcrumbs chain
    const fetchBreadcrumbs = useCallback(async () => {
        if (sidebar !== "home" || !parent) return setBreadcrumbs([]);
        try {
            const crumbs = await buildBreadcrumbs(parent, token);
            setBreadcrumbs(crumbs);
        } catch {
            setBreadcrumbs([]);
        }
    }, [parent, token, sidebar]);

    // Storage usage
    const fetchUsage = useCallback(async () => {
        try {
            const { data } = await axios.get("/api/files/usage", {
                headers: { Authorization: `Bearer ${token}` },
            });
            setUsage(data);
        } catch {
            setUsage({ used: 0, quota: 5 * 1024 * 1024 * 1024 });
        }
    }, [token]);

    // Fetch Google Drive files
    const fetchGoogleFiles = useCallback(async () => {
        setGoogleLoading(true);
        try {
            const { data } = await axios.get("/api/integrations/google/list", {
                headers: { Authorization: `Bearer ${token}` },
                params: { folderId: googleParent || 'root' }
            });
            setGoogleFiles(data);
            setIsGoogleConnected(true);
        } catch (err) {
            if (err.response && err.response.status === 401) {
                setIsGoogleConnected(false);
            } else {
                console.error("Failed to fetch Google Drive files", err);
            }
        } finally {
            setGoogleLoading(false);
        }
    }, [token, googleParent]);

    const handleGoogleConnect = async () => {
        try {
            const { data } = await axios.get("/api/integrations/google/auth", {
                headers: { Authorization: `Bearer ${token}` }
            });
            window.location.href = data.url;
        } catch (err) {
            console.error("Failed to get Google Auth URL", err);
        }
    };

    const handleGoogleFolderClick = (file) => {
        setGoogleBreadcrumbs(prev => {
            const base = prev.length === 0 ? [{ id: 'root', name: 'My Drive' }] : prev;
            return [...base, { id: file.id, name: file.name }];
        });
        setGoogleParent(file.id);
    };

    const enterGoogleFolder = (folder) => {
        handleGoogleFolderClick(folder);
    };

    const handleGoogleUp = () => {
        if (googleBreadcrumbs.length === 0) return;

        const newCrumbs = [...googleBreadcrumbs];
        newCrumbs.pop(); // Remove current
        const parent = newCrumbs[newCrumbs.length - 1]; // New parent

        setGoogleBreadcrumbs(newCrumbs);
        if (newCrumbs.length === 0) {
            setGoogleParent(null);
        } else if (parent.id === 'root') {
            setGoogleParent(null); // Explicitly root
        } else {
            setGoogleParent(parent.id);
        }
    };

    const handleGoogleBreadcrumbClick = (crumb, index) => {
        const newCrumbs = googleBreadcrumbs.slice(0, index + 1);
        setGoogleBreadcrumbs(newCrumbs);
        setGoogleParent(crumb.id === 'root' ? null : crumb.id);
    };

    const handleGoogleDisconnect = async () => {
        try {
            await axios.post("/api/integrations/google/disconnect", {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setIsGoogleConnected(false);
            setGoogleFiles([]);
            setSidebar("home");
            setGoogleParent(null);
            setGoogleBreadcrumbs([]);
        } catch (err) {
            console.error("Failed to disconnect Google Drive", err);
        }
    };

    const handleGoogleImport = async (file) => {
        // file: { id, name, mimeType, size }
        // We trigger an import to the current parent folder (or root if null)
        // However, the import endpoint currently purely puts it in uploads/userId root or creates a file record.
        // Let's assume it puts it in root for now, or we can update the backend to accept parent.
        // For now, let's just call import.

        // Show some loading indicator?
        // Let's just alert for now or use a snackbar if available (not in this file).
        // We'll set uploading true to show the progress bar generally? 
        // Or just await.
        const originalText = "Importing...";
        // Simple blocked UI for now
        if (!window.confirm(`Import "${file.name}" to your files?`)) return;

        try {
            await axios.post("/api/integrations/google/import", {
                fileId: file.id,
                name: file.name,
                mimeType: file.mimeType,
                size: file.size
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            alert("Import successful!");
            // Optionally switch to home to see it
            // setSidebar("home");
            // fetchFiles();
        } catch (err) {
            alert("Import failed.");
            console.error(err);
        }
    };

    const handleGoogleCreateProject = async (file) => {
        // Similar to import, but we mark as temporary and redirect
        const originalText = "Creating Project...";
        if (!window.confirm(`Import "${file.name}" and create project?`)) return;

        try {
            const { data: newFile } = await axios.post("/api/integrations/google/import", {
                fileId: file.id,
                name: file.name,
                mimeType: file.mimeType,
                size: file.size,
                isTemporary: true
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            // Redirect to new project page
            navigate('/dashboard/new-translation', { state: { fileId: newFile._id, isTemporary: true } });
        } catch (err) {
            alert("Failed to prepare file for project.");
            console.error(err);
        }
    };

    // --- Dropbox Handlers ---

    const fetchDropboxFiles = useCallback(async () => {
        setDropboxLoading(true);
        try {
            const { data } = await axios.get("/api/dropbox/list", {
                headers: { Authorization: `Bearer ${token}` },
                params: { path: dropboxParent || '' }
            });
            setDropboxFiles(data);
            setIsDropboxConnected(true);
        } catch (err) {
            if (err.response && err.response.status === 401) {
                setIsDropboxConnected(false);
            } else {
                console.error("Failed to fetch Dropbox files", err);
            }
        } finally {
            setDropboxLoading(false);
        }
    }, [token, dropboxParent]);

    const handleDropboxConnect = async () => {
        try {
            const { data } = await axios.get("/api/dropbox/auth", {
                headers: { Authorization: `Bearer ${token}` }
            });
            window.location.href = data.url;
        } catch (err) {
            console.error("Failed to get Dropbox Auth URL", err);
        }
    };

    const handleDropboxFolderClick = (file) => {
        // file: { id, name, path_display }
        setDropboxBreadcrumbs(prev => {
            const base = prev.length === 0 ? [{ id: '', name: 'Dropbox' }] : prev;
            return [...base, { id: file.path_display, name: file.name }]; // Use path_display as ID for Dropbox
        });
        setDropboxParent(file.path_display);
    };

    const handleDropboxUp = () => {
        if (dropboxBreadcrumbs.length === 0) return;
        const newCrumbs = [...dropboxBreadcrumbs];
        newCrumbs.pop();
        const parent = newCrumbs[newCrumbs.length - 1];
        setDropboxBreadcrumbs(newCrumbs);

        if (newCrumbs.length === 0 || parent.id === '') {
            setDropboxParent("");
        } else {
            setDropboxParent(parent.id);
        }
    };

    const handleDropboxBreadcrumbClick = (crumb, index) => {
        const newCrumbs = dropboxBreadcrumbs.slice(0, index + 1);
        setDropboxBreadcrumbs(newCrumbs);
        setDropboxParent(crumb.id === '' ? "" : crumb.id);
    };

    const handleDropboxDisconnect = async () => {
        try {
            await axios.post("/api/dropbox/disconnect", {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setIsDropboxConnected(false);
            setDropboxFiles([]);
            if (sidebar === 'dropbox') setSidebar("home");
            setDropboxParent("");
            setDropboxBreadcrumbs([]);
        } catch (err) {
            console.error("Failed to disconnect Dropbox", err);
        }
    };

    const handleDropboxImport = async (file) => {
        if (!window.confirm(`Import "${file.name}" to your files?`)) return;
        try {
            await axios.post("/api/dropbox/import", {
                path: file.path_display || file.id, // Dropbox needs path (usually path_lower or id depending on API, using path_display/id from list)
                name: file.name,
                size: file.size
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            alert("Import successful!");
        } catch (err) {
            alert("Import failed.");
            console.error(err);
        }
    };

    const handleDropboxCreateProject = async (file) => {
        if (!window.confirm(`Import "${file.name}" and create project?`)) return;
        try {
            const { data: newFile } = await axios.post("/api/dropbox/import", {
                path: file.path_display || file.id,
                name: file.name,
                size: file.size,
                isTemporary: true
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            navigate('/dashboard/new-translation', { state: { fileId: newFile._id, isTemporary: true } });
        } catch (err) {
            alert("Failed to prepare file for project.");
            console.error(err);
        }
    };

    // --- OneDrive Handlers ---

    const fetchOneDriveFiles = useCallback(async () => {
        setOneDriveLoading(true);
        try {
            const { data } = await axios.get("/api/integrations/onedrive/files", {
                headers: { Authorization: `Bearer ${token}` },
                // OneDrive service uses parent folder ID if needed, 
                // but currently lists root or accepts folderId if we add it.
                // For now, list root.
                params: { folderId: oneDriveParent || 'root' }
            });
            setOneDriveFiles(data);
            setIsOneDriveConnected(true);
        } catch (err) {
            if (err.response && err.response.status === 401) {
                setIsOneDriveConnected(false);
            } else {
                console.error("Failed to fetch OneDrive files", err);
            }
        } finally {
            setOneDriveLoading(false);
        }
    }, [token, oneDriveParent]);

    const handleOneDriveConnect = async () => {
        try {
            const { data } = await axios.get("/api/integrations/onedrive/auth", {
                headers: { Authorization: `Bearer ${token}` }
            });
            window.location.href = data.url;
        } catch (err) {
            console.error("Failed to get OneDrive Auth URL", err);
        }
    };

    const handleOneDriveFolderClick = (file) => {
        setOneDriveBreadcrumbs(prev => {
            const base = prev.length === 0 ? [{ id: null, name: 'OneDrive' }] : prev;
            return [...base, { id: file.id, name: file.name }];
        });
        setOneDriveParent(file.id);
    };

    const handleOneDriveUp = () => {
        if (oneDriveBreadcrumbs.length <= 1) {
            setOneDriveParent(null);
            setOneDriveBreadcrumbs([]);
            return;
        }
        const newCrumbs = [...oneDriveBreadcrumbs];
        newCrumbs.pop();
        const parent = newCrumbs[newCrumbs.length - 1];
        setOneDriveBreadcrumbs(newCrumbs);
        setOneDriveParent(parent.id);
    };

    const handleOneDriveBreadcrumbClick = (crumb, index) => {
        const newCrumbs = oneDriveBreadcrumbs.slice(0, index + 1);
        setOneDriveBreadcrumbs(newCrumbs);
        setOneDriveParent(crumb.id);
    };

    const handleOneDriveDisconnect = async () => {
        try {
            // Updated to use onedrive disconnect if exists, 
            // but we can just use the generic integration delete if we want.
            // For now, let's assume we use the onedrive routes.
            await axios.delete("/api/integrations/onedrive", {
                headers: { Authorization: `Bearer ${token}` }
            });
            setIsOneDriveConnected(false);
            setOneDriveFiles([]);
            if (sidebar === 'onedrive') setSidebar("home");
        } catch (err) {
            console.error("Failed to disconnect OneDrive", err);
        }
    };

    const handleOneDriveImport = async (file) => {
        if (!window.confirm(`Import "${file.name}" to your files?`)) return;
        try {
            await axios.post("/api/integrations/onedrive/import", {
                itemId: file.id,
                name: file.name,
                size: file.size
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            alert("Import successful!");
            fetchFiles(); // Refresh local files
        } catch (err) {
            alert("Import failed.");
            console.error(err);
        }
    };

    const handleOneDriveCreateProject = async (file) => {
        if (!window.confirm(`Import "${file.name}" and create project?`)) return;
        try {
            const { data: newFile } = await axios.post("/api/integrations/onedrive/import", {
                itemId: file.id,
                name: file.name,
                size: file.size,
                isTemporary: true
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            navigate('/dashboard/new-translation', { state: { fileId: newFile._id, isTemporary: true } });
        } catch (err) {
            alert("Failed to prepare file for project.");
            console.error(err);
        }
    };

    useEffect(() => {
        if (sidebar === "google") {
            fetchGoogleFiles();
        } else if (sidebar === "dropbox") {
            fetchDropboxFiles();
        } else if (sidebar === "onedrive") {
            fetchOneDriveFiles();
        } else {
            fetchFiles();
            fetchBreadcrumbs();
            fetchUsage();
        }
    }, [fetchFiles, fetchBreadcrumbs, fetchUsage, fetchGoogleFiles, fetchDropboxFiles, fetchOneDriveFiles, sidebar]);

    // Drag & Drop Upload
    const handleDrop = async (e) => {
        e.preventDefault();
        setUploading(true);
        setUploadProgress(0);

        const filesDropped = Array.from(e.dataTransfer.files);
        const formData = new FormData();
        filesDropped.forEach((file) => formData.append("files", file));
        formData.append("parent", parent || "");
        try {
            await axios.post("/api/files/upload", formData, {
                headers: { Authorization: `Bearer ${token}` },
                onUploadProgress: (progressEvent) => {
                    if (progressEvent.total) {
                        const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        setUploadProgress(percent);
                    }
                },
            });
            fetchFiles();
            fetchUsage();
        } finally {
            setUploading(false);
            setUploadProgress(0);
        }
    };

    // Click Upload
    const handleFileInput = async (e) => {
        setUploading(true);
        setUploadProgress(0);

        const filesInput = Array.from(e.target.files);
        const formData = new FormData();
        filesInput.forEach((file) => formData.append("files", file));
        formData.append("parent", parent || "");
        try {
            await axios.post("/api/files/upload", formData, {
                headers: { Authorization: `Bearer ${token}` },
                onUploadProgress: (progressEvent) => {
                    if (progressEvent.total) {
                        const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        setUploadProgress(percent);
                    }
                },
            });
            fetchFiles();
            fetchUsage();
        } finally {
            setUploading(false);
            setUploadProgress(0);
        }
    };

    // Create folder
    const handleCreateFolder = async () => {
        if (!newFolderName.trim()) return;
        await axios.post(
            "/api/files/folder",
            { name: newFolderName, parent },
            { headers: { Authorization: `Bearer ${token}` } }
        );
        setNewFolderName("");
        setFolderDialog(false);
        fetchFiles();
    };

    // Download file
    const handleDownload = async (file) => {
        const res = await axios.get(`/api/files/download/${file._id}`, {
            headers: { Authorization: `Bearer ${token}` },
            responseType: "blob",
        });
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", file.name);
        document.body.appendChild(link);
        link.click();
        link.remove();
    };

    // Delete (move to bin)
    const handleDelete = async (file) => {
        if (!window.confirm(`Move "${file.name}" to Bin?`)) return;
        await axios.put(`/api/files/${file._id}/bin`, {}, {
            headers: { Authorization: `Bearer ${token}` },
        });
        fetchFiles();
        fetchUsage();
    };

    const handleRename = async (file) => {
        const newName = window.prompt("Rename to:", file.name);
        if (!newName || newName === file.name) return;
        await axios.put(`/api/files/${file._id}/rename`, { name: newName }, {
            headers: { Authorization: `Bearer ${token}` },
        });
        fetchFiles();
    };

    // Go into folder
    const enterFolder = (file) => {
        setParent(file._id);
    };

    // Go up
    const goUp = async () => {
        if (!parent) return;
        const { data } = await axios.get(`/api/files/${parent}`, {
            headers: { Authorization: `Bearer ${token}` }
        });
        setParent(data && data.parent ? data.parent : null);
    };

    // Handle drag events
    const prevent = (e) => e.preventDefault();

    // Restore file from bin
    const handleRestore = async (file) => {
        await axios.put(`/api/files/${file._id}/restore`, {}, {
            headers: { Authorization: `Bearer ${token}` },
        });
        fetchFiles();
        fetchUsage();
    };

    // Permanently delete from bin
    const handlePermanentDelete = async (file) => {
        if (!window.confirm(`Permanently delete "${file.name}"? This cannot be undone!`)) return;
        await axios.delete(`/api/files/${file._id}?permanent=true`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        fetchFiles();
        fetchUsage();
    };

    // ---- Sharing Dialog (with Team Members) ----
    const openShareDialog = async (file) => {
        setShareTargetFile(file);
        setShareError("");
        setSelectedMember("");
        setShareDialogOpen(true);
        try {
            const { data } = await axios.get("/api/team", {
                headers: { Authorization: `Bearer ${token}` },
            });
            // Only show 'active' team members with populated userId
            const activeMembers = data.filter(
                (t) => t.status === "active" && t.userId && typeof t.userId === "object"
            );
            setTeamMembers(activeMembers);
        } catch {
            setTeamMembers([]);
        }
    };

    const handleShareFile = async () => {
        if (!selectedMember) {
            setShareError("Please select a team member.");
            return;
        }
        try {
            await axios.put(
                `/api/files/${shareTargetFile._id}/share`,
                { shareWith: selectedMember },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setShareDialogOpen(false);
            setShareTargetFile(null);
            setSelectedMember("");
            setShareError("");
            fetchFiles();
        } catch (err) {
            setShareError(err.response?.data?.msg || "Failed to share file.");
        }
    };

    const openExternalShareDialog = (file) => {
        setExternalShareTargetFile(file);
        setExternalEmail("");
        setExternalShareError("");
        setExternalShareDialogOpen(true);
    };

    const handleExternalShare = async () => {
        if (!externalEmail.trim()) {
            setExternalShareError("Please enter an email address.");
            return;
        }
        try {
            await axios.post(
                `/api/files/${externalShareTargetFile._id}/external-share-email`,
                { email: externalEmail },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setExternalShareDialogOpen(false);
            setExternalShareTargetFile(null);
            setExternalEmail("");
            setExternalShareError("");
        } catch (err) {
            setExternalShareError(err.response?.data?.msg || "Failed to send link.");
        }
    };

    const handleSearch = async () => {
        if (!searchQuery.trim()) { await fetchFiles(); return; }
        try {
            const { data } = await axios.get('/api/files/search', {
                params: { q: searchQuery },
                headers: { Authorization: `Bearer ${token}` }
            });
            setFiles(data);
        } catch {
            setFiles([]);
        }
    };

    // When switching view, reset parent and breadcrumbs
    useEffect(() => {
        if (sidebar !== "home") setParent(null);
    }, [sidebar]);

    // File icon renderer with react-file-icon
    function renderFileIcon(file) {
        if (file.type === "folder") return <FolderIcon color="primary" />;
        const ext = getFileExtension(file.name);
        if (defaultStyles[ext]) {
            return (
                <Box sx={{ width: 32 }}>
                    <FileIcon extension={ext} {...defaultStyles[ext]} />
                </Box>
            );
        }
        // fallback
        return (
            <Box sx={{ width: 32 }}>
                <FileIcon extension={ext || "file"} />
            </Box>
        );
    }

    function renderActions(file) {
        if (sidebar === "bin") {
            return (
                <>
                    <Tooltip title="Restore">
                        <IconButton
                            onClick={() => handleRestore(file)}
                            color="primary"
                            size="small"
                        >
                            <RestoreIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete forever">
                        <IconButton
                            onClick={() => handlePermanentDelete(file)}
                            color="error"
                            size="small"
                        >
                            <DeleteSweepIcon />
                        </IconButton>
                    </Tooltip>
                </>
            );
        }
        return (
            <>
                {file.type === "file" && (
                    <>
                        <Tooltip title="Download">
                            <IconButton onClick={() => handleDownload(file)} size="small">
                                <CloudDownloadIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Create Project">
                            <IconButton
                                onClick={() =>
                                    navigate('/dashboard/new-translation', { state: { fileId: file._id } })
                                }
                                size="small"
                            >
                                <TranslateIcon />
                            </IconButton>
                        </Tooltip>
                    </>
                )}
                <Tooltip title="Rename">
                    <IconButton onClick={() => handleRename(file)} size="small">
                        <DriveFileRenameOutlineIcon />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Share">
                    <IconButton onClick={() => openShareDialog(file)} size="small">
                        <ShareIcon color="primary" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Email Share Link">
                    <IconButton onClick={() => openExternalShareDialog(file)} size="small">
                        <MailOutlineIcon color="primary" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                    <IconButton
                        onClick={() => handleDelete(file)}
                        color="error"
                        size="small"
                    >
                        <DeleteIcon />
                    </IconButton>
                </Tooltip>
            </>
        );
    }

    // --- Sidebar: Show plan + usage
    const planName = getPlanFromQuota(usage.quota);

    const Sidebar = (
        <Box sx={{
            width: 240,
            minHeight: "100%",
            borderRight: "1px solid #e0e0e0",
            background: "#f8fafd",
            display: "flex",
            flexDirection: "column"
        }}>
            <Typography variant="h6" sx={{ p: 2, pb: 1 }}>
                File Manager
            </Typography>
            <List>
                <ListItem
                    button
                    selected={sidebar === "home"}
                    onClick={() => { setSidebar("home"); setParent(null); }}
                >
                    <ListItemIcon><HomeIcon /></ListItemIcon>
                    <ListItemText primary="Home" />
                </ListItem>
                <ListItem
                    button
                    selected={sidebar === "shared"}
                    onClick={() => { setSidebar("shared"); setParent(null); }}
                >
                    <ListItemIcon><FolderSharedIcon /></ListItemIcon>
                    <ListItemText primary="Shared with me" />
                </ListItem>
                <ListItem
                    button
                    selected={sidebar === "bin"}
                    onClick={() => { setSidebar("bin"); setParent(null); }}
                    sx={{ color: "#ca2121" }}
                >
                    <ListItemIcon><DeleteIcon sx={{ color: "#ca2121" }} /></ListItemIcon>
                    <ListItemText primary="Bin" />
                </ListItem>
            </List>
            <Divider sx={{ my: 1 }} />
            <List>
                <ListItem
                    button
                    selected={sidebar === "google"}
                    onClick={() => {
                        setSidebar("google");
                        setParent(null);
                    }}
                >
                    <ListItemIcon>
                        <Box component="img" src="/assets/logo/googledrive-icon.png" alt="Google Drive" sx={{ width: 24, height: 24 }} />
                    </ListItemIcon>
                    <ListItemText primary="Google Drive" />
                </ListItem>
                <ListItem
                    button
                    selected={sidebar === "dropbox"}
                    onClick={() => {
                        setSidebar("dropbox");
                        setDropboxParent("");
                    }}
                >
                    <ListItemIcon>
                        <Box component="img" src="/assets/logo/dropbox-logo.png" alt="Dropbox" sx={{ width: 24, height: 24, objectFit: 'contain' }} />
                    </ListItemIcon>
                    <ListItemText primary="Dropbox" />
                </ListItem>
                <ListItem
                    button
                    selected={sidebar === "onedrive"}
                    onClick={() => {
                        setSidebar("onedrive");
                        setOneDriveParent(null);
                    }}
                >
                    <ListItemIcon>
                        <Box component="img" src="/assets/logo/onedrive-logo.png" alt="OneDrive" sx={{ width: 24, height: 24, objectFit: 'contain' }} />
                    </ListItemIcon>
                    <ListItemText primary="OneDrive" />
                </ListItem>
            </List>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ p: 2, pt: 1, mt: "auto" }}>
                <Typography variant="caption" color="primary" sx={{ fontWeight: 600 }}>
                    {planName}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", mb: 0.7 }}>
                    <StorageIcon sx={{ mr: 1 }} fontSize="small" />
                    <Typography variant="body2">
                        Storage used:
                    </Typography>
                </Box>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
                    {formatBytes(usage.used)} / {formatBytes(usage.quota)}
                </Typography>
                <LinearProgress
                    variant="determinate"
                    value={Math.min((usage.used / usage.quota) * 100, 100)}
                    sx={{ height: 8, borderRadius: 2, background: '#dde5f5' }}
                />
            </Box>
        </Box>
    );

    return (
        <Box sx={{ display: "flex", height: "calc(100vh - 80px)" }}>
            {Sidebar}
            <Box
                sx={{
                    flex: 1,
                    p: 3,
                    overflow: "auto",
                    background: "#f6f9fd"
                }}
            >
                {/* Breadcrumbs */}
                {sidebar === "home" && (
                    <Breadcrumbs sx={{ mb: 2 }}>
                        <Button size="small" onClick={() => setParent(null)}>
                            Root
                        </Button>
                        {breadcrumbs.map((f, idx) => (
                            <Button
                                size="small"
                                key={f._id}
                                onClick={() => setParent(f._id)}
                            >
                                {f.name}
                            </Button>
                        ))}
                    </Breadcrumbs>
                )}

                {/* Header */}
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Typography variant="h5" sx={{ flexGrow: 1 }}>
                        {sidebar === "home"
                            ? "My Files"
                            : sidebar === "shared"
                                ? "Shared with Me"
                                : sidebar === "bin"
                                    ? "Bin"
                                    : sidebar === "google"
                                        ? "Google Drive"
                                        : sidebar === "onedrive"
                                            ? "OneDrive"
                                            : "Dropbox"
                        }
                    </Typography>
                    {sidebar === "google" && isGoogleConnected && (
                        <>
                            {/* Google Breadcrumbs if distinct from title, or replace title? */}
                            {/* Let's keep title as "Google Drive" and add up button / breadcrumbs below or in header */}

                            {/* Actually, let's put "Up" button here if deeply nested */}
                            <Button
                                startIcon={<ArrowUpwardIcon />}
                                variant="outlined"
                                size="small"
                                disabled={!googleParent || googleParent === 'root'}
                                onClick={handleGoogleUp}
                                sx={{ mr: 1 }}
                            >
                                Up
                            </Button>
                            <Button color="error" size="small" onClick={handleGoogleDisconnect}>
                                Disconnect
                            </Button>
                        </>
                    )}
                    {sidebar === "home" && (
                        <>
                            <Button
                                startIcon={<AddCircleOutlineIcon />}
                                variant="contained"
                                size="small"
                                sx={{ mr: 1 }}
                                onClick={() => setFolderDialog(true)}
                            >
                                New Folder
                            </Button>
                            <Button
                                startIcon={<ArrowUpwardIcon />}
                                variant="outlined"
                                size="small"
                                disabled={!parent}
                                onClick={goUp}
                            >
                                Up
                            </Button>
                            <TextField
                                size="small"
                                placeholder="Search"
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') handleSearch(); }}
                                sx={{ ml: 1 }}
                                InputProps={{
                                    endAdornment: (
                                        <IconButton size="small" onClick={handleSearch}>
                                            <SearchIcon />
                                        </IconButton>
                                    )
                                }}
                            />
                        </>
                    )}
                    {sidebar === "onedrive" && isOneDriveConnected && (
                        <Button color="error" size="small" onClick={handleOneDriveDisconnect}>
                            Disconnect
                        </Button>
                    )}
                </Box>

                {/* Upload Drop Area */}
                {sidebar === "home" && (
                    <>
                        {uploading && (
                            <Box sx={{ mb: 2 }}>
                                <LinearProgress variant="determinate" value={uploadProgress} />
                                <Typography variant="caption">{uploadProgress}%</Typography>
                            </Box>
                        )}
                        <Box
                            onDrop={handleDrop}
                            onDragOver={prevent}
                            onDragEnter={prevent}
                            onDragLeave={prevent}
                            sx={{
                                border: "2px dashed #2196f3",
                                py: 4,
                                px: 2,
                                mb: 3,
                                borderRadius: 2,
                                textAlign: "center",
                                color: uploading ? "#2196f3" : "#757575",
                                bgcolor: uploading ? "#e3f2fd" : "#fafbfc",
                                transition: "background 0.2s"
                            }}
                        >
                            <Typography>
                                Drag & drop files here to upload, or&nbsp;
                                <Button component="label" variant="outlined" size="small">
                                    Choose Files
                                    <Input
                                        type="file"
                                        multiple
                                        sx={{ display: "none" }}
                                        onChange={handleFileInput}
                                    />
                                </Button>
                            </Typography>
                        </Box>
                    </>
                )}

                {/* New Folder Dialog */}
                {folderDialog && (
                    <Box sx={{ display: "flex", mb: 2, gap: 1 }}>
                        <TextField
                            size="small"
                            label="Folder Name"
                            value={newFolderName}
                            onChange={e => setNewFolderName(e.target.value)}
                            autoFocus
                        />
                        <Button
                            variant="contained"
                            size="small"
                            onClick={handleCreateFolder}
                        >
                            Create
                        </Button>
                        <Button
                            size="small"
                            onClick={() => setFolderDialog(false)}
                        >
                            Cancel
                        </Button>
                    </Box>
                )}

                {/* File/Folder List (Hide if in Google Drive, Dropbox, or OneDrive view) */}
                {sidebar !== "google" && sidebar !== "dropbox" && sidebar !== "onedrive" && (
                    <List>
                        {files.map((file) => (
                            <ListItem
                                key={file._id}
                                sx={{ borderBottom: "1px solid #f0f0f0" }}
                                secondaryAction={renderActions(file)}
                                button={sidebar === "home" && file.type === "folder"}
                                onClick={() =>
                                    sidebar === "home" && file.type === "folder" && enterFolder(file)
                                }
                            >
                                <ListItemIcon>
                                    {renderFileIcon(file)}
                                </ListItemIcon>
                                <ListItemText
                                    primary={file.name}
                                    secondary={getFileSecondaryText(file)}
                                />
                            </ListItem>
                        ))}
                        {files.length === 0 && (
                            <Typography sx={{ color: "#999", mt: 2, ml: 2 }}>
                                No files or folders.
                            </Typography>
                        )}
                        {files.length === 0 && (
                            <Typography sx={{ color: "#999", mt: 2, ml: 2 }}>
                                No files or folders.
                            </Typography>
                        )}
                    </List>
                )}

                {/* Google Drive View */}
                {sidebar === "google" && (
                    <Box>
                        {!isGoogleConnected ? (
                            <Box sx={{ textAlign: 'center', mt: 5 }}>
                                <CloudQueueIcon sx={{ fontSize: 60, color: '#ccc', mb: 2 }} />
                                <Typography variant="h6" gutterBottom>
                                    Connect Google Drive
                                </Typography>
                                <Typography color="textSecondary" sx={{ mb: 3 }}>
                                    Access your Google Drive files and import them directly into Rush Studio.
                                </Typography>
                                <Button
                                    variant="contained"
                                    startIcon={<CloudQueueIcon />}
                                    onClick={handleGoogleConnect}
                                    sx={{ mr: 1 }}
                                >
                                    Connect Google Drive
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={() => navigate('/dashboard/apps')}
                                >
                                    Go to Apps Marketplace
                                </Button>
                            </Box>
                        ) : (
                            <Box>
                                {/* Breadcrumbs for Google */}
                                {googleBreadcrumbs.length > 0 && (
                                    <Breadcrumbs sx={{ mb: 2, px: 2 }}>
                                        {googleBreadcrumbs.map((crumb, idx) => (
                                            <Button
                                                size="small"
                                                key={crumb.id}
                                                onClick={() => handleGoogleBreadcrumbClick(crumb, idx)}
                                                disabled={idx === googleBreadcrumbs.length - 1} // Disable current
                                            >
                                                {crumb.name}
                                            </Button>
                                        ))}
                                    </Breadcrumbs>
                                )}
                                <List>
                                    {googleLoading && <LinearProgress />}
                                    {!googleLoading && googleFiles.length === 0 && (
                                        <Typography sx={{ p: 2, color: '#777' }}>No files found in Google Drive.</Typography>
                                    )}
                                    {googleFiles.map((file) => (
                                        <ListItem
                                            key={file.id}
                                            sx={{ borderBottom: "1px solid #f0f0f0" }}
                                            button={file.mimeType === 'application/vnd.google-apps.folder'}
                                            onClick={() => {
                                                if (file.mimeType === 'application/vnd.google-apps.folder') {
                                                    enterGoogleFolder(file);
                                                }
                                            }}
                                            secondaryAction={
                                                file.mimeType !== 'application/vnd.google-apps.folder' && (
                                                    <>
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            onClick={() => handleGoogleImport(file)}
                                                        >
                                                            Import
                                                        </Button>
                                                        <Tooltip title="Create Project">
                                                            <IconButton
                                                                onClick={() => handleGoogleCreateProject(file)}
                                                                size="small"
                                                                sx={{ ml: 1 }}
                                                            >
                                                                <TranslateIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </>
                                                )
                                            }
                                        >
                                            <ListItemIcon>
                                                <Box sx={{ width: 24, display: 'flex', justifyContent: 'center' }}>
                                                    {file.mimeType === 'application/vnd.google-apps.folder' ?
                                                        <FolderIcon color="primary" /> :
                                                        <FileIcon extension={getFileExtension(file.name)} {...defaultStyles[getFileExtension(file.name)]} />
                                                    }
                                                </Box>
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={file.name}
                                                secondary={
                                                    <span>
                                                        {file.size ? formatFileSize(file.size) : 'Unknown size'} • {new Date(file.createdTime).toLocaleDateString()}
                                                    </span>
                                                }
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </Box>
                        )}
                    </Box>
                )
                }

                {/* Dropbox View */}
                {sidebar === "dropbox" && (
                    <Box>
                        {!isDropboxConnected ? (
                            <Box sx={{ textAlign: 'center', mt: 5 }}>
                                <Box component="img" src="/assets/logo/dropbox-logo.png" alt="Dropbox" sx={{ width: 60, height: 60, mb: 2, opacity: 0.5, objectFit: 'contain' }} />
                                <Typography variant="h6" gutterBottom>
                                    Connect Dropbox
                                </Typography>
                                <Typography color="textSecondary" sx={{ mb: 3 }}>
                                    Access your Dropbox files and import them directly into Rush Studio.
                                </Typography>
                                <Button
                                    variant="contained"
                                    startIcon={<Box component="img" src="/assets/logo/dropbox-logo.png" alt="Dropbox" sx={{ width: 20, height: 20, objectFit: 'contain', filter: 'brightness(0) invert(1)' }} />}
                                    onClick={handleDropboxConnect}
                                    sx={{ mr: 1 }}
                                >
                                    Connect Dropbox
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={() => navigate('/dashboard/apps')}
                                >
                                    Go to Apps Marketplace
                                </Button>
                            </Box>
                        ) : (
                            <Box>
                                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                                    {/* Breadcrumbs for Dropbox */}
                                    {dropboxBreadcrumbs.length > 0 && (
                                        <Breadcrumbs sx={{ flexGrow: 1, px: 2 }}>
                                            {dropboxBreadcrumbs.map((crumb, idx) => (
                                                <Button
                                                    size="small"
                                                    key={crumb.id || 'root'}
                                                    onClick={() => handleDropboxBreadcrumbClick(crumb, idx)}
                                                    disabled={idx === dropboxBreadcrumbs.length - 1}
                                                >
                                                    {crumb.name}
                                                </Button>
                                            ))}
                                        </Breadcrumbs>
                                    )}
                                    <Button
                                        startIcon={<ArrowUpwardIcon />}
                                        variant="outlined"
                                        size="small"
                                        disabled={!dropboxParent}
                                        onClick={handleDropboxUp}
                                        sx={{ mr: 1, ml: 'auto' }}
                                    >
                                        Up
                                    </Button>
                                    <Button color="error" size="small" onClick={handleDropboxDisconnect}>
                                        Disconnect
                                    </Button>
                                </Box>

                                <List>
                                    {dropboxLoading && <LinearProgress />}
                                    {!dropboxLoading && dropboxFiles.length === 0 && (
                                        <Typography sx={{ p: 2, color: '#777' }}>No files found in Dropbox.</Typography>
                                    )}
                                    {dropboxFiles.map((file) => (
                                        <ListItem
                                            key={file.id}
                                            sx={{ borderBottom: "1px solid #f0f0f0" }}
                                            button={file.type === 'folder'}
                                            onClick={() => {
                                                if (file.type === 'folder') {
                                                    handleDropboxFolderClick(file);
                                                }
                                            }}
                                            secondaryAction={
                                                file.type !== 'folder' && (
                                                    <>
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            onClick={() => handleDropboxImport(file)}
                                                        >
                                                            Import
                                                        </Button>
                                                        <Tooltip title="Create Project">
                                                            <IconButton
                                                                onClick={() => handleDropboxCreateProject(file)}
                                                                size="small"
                                                                sx={{ ml: 1 }}
                                                            >
                                                                <TranslateIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </>
                                                )
                                            }
                                        >
                                            <ListItemIcon>
                                                <Box sx={{ width: 24, display: 'flex', justifyContent: 'center' }}>
                                                    {file.type === 'folder' ?
                                                        <FolderIcon color="primary" /> :
                                                        <FileIcon extension={getFileExtension(file.name)} {...defaultStyles[getFileExtension(file.name)]} />
                                                    }
                                                </Box>
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={file.name}
                                                secondary={
                                                    <span>
                                                        {file.size ? formatFileSize(file.size) : 'Unknown size'} • {file.client_modified ? new Date(file.client_modified).toLocaleDateString() : ''}
                                                    </span>
                                                }
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </Box>
                        )}
                    </Box>
                )}

                {/* OneDrive View */}
                {sidebar === "onedrive" && (
                    <Box>
                        {!isOneDriveConnected ? (
                            <Box sx={{ textAlign: 'center', mt: 5 }}>
                                <Box component="img" src="/assets/logo/onedrive-logo.png" alt="OneDrive" sx={{ width: 60, height: 60, mb: 2, opacity: 0.5, objectFit: 'contain' }} />
                                <Typography variant="h6" gutterBottom>
                                    Connect OneDrive
                                </Typography>
                                <Typography color="textSecondary" sx={{ mb: 3 }}>
                                    Access your OneDrive files and import them directly into Rush Studio.
                                </Typography>
                                <Button
                                    variant="contained"
                                    startIcon={<Box component="img" src="/assets/logo/onedrive-logo.png" alt="OneDrive" sx={{ width: 20, height: 20, objectFit: 'contain', filter: 'brightness(0) invert(1)' }} />}
                                    onClick={handleOneDriveConnect}
                                    sx={{ mr: 1 }}
                                >
                                    Connect OneDrive
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={() => navigate('/dashboard/apps')}
                                >
                                    Go to Apps Marketplace
                                </Button>
                            </Box>
                        ) : (
                            <Box>
                                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                                    {/* Breadcrumbs for OneDrive */}
                                    {oneDriveBreadcrumbs.length > 0 && (
                                        <Breadcrumbs sx={{ flexGrow: 1, px: 2 }}>
                                            {oneDriveBreadcrumbs.map((crumb, idx) => (
                                                <Button
                                                    size="small"
                                                    key={crumb.id || 'root'}
                                                    onClick={() => handleOneDriveBreadcrumbClick(crumb, idx)}
                                                    disabled={idx === oneDriveBreadcrumbs.length - 1}
                                                >
                                                    {crumb.name}
                                                </Button>
                                            ))}
                                        </Breadcrumbs>
                                    )}
                                    <Button
                                        startIcon={<ArrowUpwardIcon />}
                                        variant="outlined"
                                        size="small"
                                        disabled={!oneDriveParent}
                                        onClick={handleOneDriveUp}
                                        sx={{ mr: 1, ml: 'auto' }}
                                    >
                                        Up
                                    </Button>
                                    <Button color="error" size="small" onClick={handleOneDriveDisconnect}>
                                        Disconnect
                                    </Button>
                                </Box>

                                <List>
                                    {oneDriveLoading && <LinearProgress />}
                                    {!oneDriveLoading && oneDriveFiles.length === 0 && (
                                        <Typography sx={{ p: 2, color: '#777' }}>No files found in OneDrive.</Typography>
                                    )}
                                    {oneDriveFiles.map((file) => (
                                        <ListItem
                                            key={file.id}
                                            sx={{ borderBottom: "1px solid #f0f0f0" }}
                                            button={!!file.folder}
                                            onClick={() => {
                                                if (file.folder) {
                                                    handleOneDriveFolderClick(file);
                                                }
                                            }}
                                            secondaryAction={
                                                !file.folder && (
                                                    <>
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            onClick={() => handleOneDriveImport(file)}
                                                        >
                                                            Import
                                                        </Button>
                                                        <Tooltip title="Create Project">
                                                            <IconButton
                                                                onClick={() => handleOneDriveCreateProject(file)}
                                                                size="small"
                                                                sx={{ ml: 1 }}
                                                            >
                                                                <TranslateIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </>
                                                )
                                            }
                                        >
                                            <ListItemIcon>
                                                <Box sx={{ width: 24, display: 'flex', justifyContent: 'center' }}>
                                                    {file.folder ?
                                                        <FolderIcon color="primary" /> :
                                                        <FileIcon extension={getFileExtension(file.name)} {...defaultStyles[getFileExtension(file.name)]} />
                                                    }
                                                </Box>
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={file.name}
                                                secondary={
                                                    <span>
                                                        {file.size ? formatFileSize(file.size) : 'Unknown size'} • {file.lastModifiedDateTime ? new Date(file.lastModifiedDateTime).toLocaleDateString() : ''}
                                                    </span>
                                                }
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </Box>
                        )}
                    </Box>
                )}

                {/* --- Share Dialog --- */}
                <Dialog open={shareDialogOpen} onClose={() => setShareDialogOpen(false)}>
                    <DialogTitle>Share {shareTargetFile?.name}</DialogTitle>
                    <DialogContent>
                        <FormControl fullWidth sx={{ mt: 1 }}>
                            <InputLabel id="share-team-member-label">Select team member</InputLabel>
                            <Select
                                labelId="share-team-member-label"
                                value={selectedMember}
                                label="Select team member"
                                onChange={e => setSelectedMember(e.target.value)}
                            >
                                {teamMembers.length === 0 && (
                                    <MenuItem disabled value="">
                                        No active team members
                                    </MenuItem>
                                )}
                                {teamMembers.map((member) => (
                                    <MenuItem value={member.userId._id} key={member.userId._id}>
                                        {member.userId.firstName || member.userId.username || member.userId.email}
                                        {member.userId.email && <> ({member.userId.email})</>}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {shareError && (
                            <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                                {shareError}
                            </Typography>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setShareDialogOpen(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleShareFile} variant="contained">
                            Share
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* --- External Share Dialog --- */}
                <Dialog open={externalShareDialogOpen} onClose={() => setExternalShareDialogOpen(false)}>
                    <DialogTitle>Email share link for {externalShareTargetFile?.name}</DialogTitle>
                    <DialogContent>
                        <TextField
                            fullWidth
                            label="Recipient Email"
                            value={externalEmail}
                            onChange={e => setExternalEmail(e.target.value)}
                            sx={{ mt: 1 }}
                        />
                        <Typography variant="body2" sx={{ mt: 1 }}>
                            The link will expire in 5 days.
                        </Typography>
                        {externalShareError && (
                            <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                                {externalShareError}
                            </Typography>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setExternalShareDialogOpen(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleExternalShare} variant="contained">
                            Send Link
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box >
        </Box >
    );
};

export default FileManager;
